import '.bz{background-color:var(--shades-blue-50);}.ssr-plugin'
import '.df{border-radius:0.5rem;}.ssr-plugin'
import '.da{padding:0.5rem;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { VerificationCodeInput } from '@app/components/core/input/text/VerificationCodeInput'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import { emailRegex } from '@app/util/helpers'
import { pages } from '@app/util/links'
import { useAuthState, useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { styled } from 'inlines'
import {
  clearStoredChallengeId,
  clearStoredPrefill,
  getStoredChallengeId,
  getStoredPrefill,
  OTC_CHALLENGE_QUERY_PARAM,
  OTC_QUERY_PARAM,
  setStoredPrefill,
  setStoredChallengeId,
} from './authQueryParams'
import {
  useState,
  KeyboardEvent,
  useCallback,
  useEffect,
  Dispatch,
} from 'react'
import { useLocation, useSearchParams } from 'wouter'

export const Login = ({
  email,
  redirectTo,
  setEmail,
  onClose,
  startFromVerification = false,
}: {
  email: string
  redirectTo?: string
  setEmail: Dispatch<React.SetStateAction<string>>
  onClose?: VoidFunction
  startFromVerification?: boolean
}) => {
  const client = useClient()
  const authState = useAuthState()
  const [, navigate] = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const challengeIdFromUrl = searchParams.get(OTC_CHALLENGE_QUERY_PARAM) ?? ''
  const storedLoginPrefill = getStoredPrefill('login')
  const [challengeId, setChallengeId] = useState<string>(
    getStoredChallengeId('login') ?? challengeIdFromUrl,
  )
  const [isEmailInvalid, setIsEmailInvalid] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(startFromVerification)
  const [showEmailField, setShowEmailField] = useState(
    !startFromVerification || !!storedLoginPrefill?.email,
  )
  const [emailIssue, setEmailIssue] = useState(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [isCodeCorrect, setIsCodeCorrect] = useState(null)
  const translations = useTranslations().app.auth

  const onEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      verifyEmail()
    }
  }

  const validateEmail = useCallback(() => {
    const isValid = emailRegex.test(email.trim())
    setIsEmailInvalid(!isValid)
    return isValid
  }, [email])

  const verifyEmail = useCallback(() => {
    if (!validateEmail()) {
      return
    }
    client
      .call('account-login', {
        email: email.trim(),
      })
      .then((result: { challengeId?: string }) => {
        if (!result?.challengeId) {
          throw new Error('Missing challenge')
        }
        setStoredChallengeId('login', result.challengeId)
        setStoredPrefill('login', {
          email: email.trim(),
        })
        setChallengeId(result.challengeId)
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set(OTC_QUERY_PARAM, 'login')
            next.delete(OTC_CHALLENGE_QUERY_PARAM)
            return next
          },
          { replace: true },
        )
      })
      .catch((e: Error) => {
        setEmailIssue(
          e.message.includes('404')
            ? translations.emailIssueNoUserMessage
            : translations.emailIssueOtherMessage,
        )
      })
    setEmailIssue(null)
    setIsEmailSent(true)
  }, [
    client,
    email,
    validateEmail,
    translations.emailIssueNoUserMessage,
    translations.emailIssueOtherMessage,
    setSearchParams,
  ])

  const onSendCodeAgain = useCallback(() => {
    setIsCodeCorrect(null)
    setVerificationCode('')
    if (!showEmailField) {
      setShowEmailField(true)
      return
    }
    verifyEmail()
  }, [showEmailField, verifyEmail, setVerificationCode, setIsCodeCorrect])

  const verifyCode = useCallback(() => {
    client
      .call('verify-one-time-code', {
        code: verificationCode,
        challengeId,
      })
      .then((r) => {
        setIsCodeCorrect(Boolean(r))
      })
      .catch((e: Error) => {
        console.warn('[auth] Login: verify-one-time-code error', {
          message: e?.message ?? String(e),
          name: e?.name,
          stack: e?.stack,
        })
        setIsCodeCorrect(false)
      })
  }, [client, verificationCode, challengeId, setIsCodeCorrect])

  const setEmailHandler = (text: string) => {
    setEmail(text)
  }

  useEffect(() => {
    const storedEmail = getStoredPrefill('login')?.email
    if (storedEmail && !email) {
      setEmail(storedEmail)
    }
  }, [email, setEmail])

  useEffect(() => {
    if (!challengeIdFromUrl) {
      return
    }
    setStoredChallengeId('login', challengeIdFromUrl)
    if (challengeId !== challengeIdFromUrl) {
      setChallengeId(challengeIdFromUrl)
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete(OTC_CHALLENGE_QUERY_PARAM)
        return next
      },
      { replace: true },
    )
  }, [challengeIdFromUrl, challengeId, setSearchParams])

  useEffect(() => {
    if (verificationCode.length === 6) {
      verifyCode()
    }
  }, [verificationCode, verifyCode])

  useEffect(() => {
    if (!isCodeCorrect) {
      return
    }
    
    clearStoredChallengeId('login')
    clearStoredPrefill('login')
    if (onClose) {
      onClose()
    } else {
      navigate(redirectTo ?? pages.home)
    }
  }, [isCodeCorrect, authState, onClose, navigate, redirectTo])

  useEffect(() => {
    if (isEmailInvalid) {
      validateEmail()
    }
  }, [email, isEmailInvalid, validateEmail])
  
  const emailSentSuccessfully = isEmailSent && !emailIssue

  return (
    <Column alignItems="stretch" style={{}}>
      <Title.H3 style={{ alignSelf: 'center' }}>
        {translations.logInTitle}
      </Title.H3>
      {showEmailField && (
        <TextInput
          label={translations.email}
          placeholder={translations.emailPlaceholder}
          text={email}
          errorMessage={
            isEmailInvalid ? translations.emailInvalidMessage : null
          }
          setText={setEmailHandler}
          onKeyDown={onEmailKeyDown}
        />
      )}
      {!emailIssue && !isEmailSent && (
        <Text.Medium color={'var(--shades-blue-500)'}>
          {translations.emailSubtitle}
        </Text.Medium>
      )}
      {emailIssue && (
        <Text.Medium isBold={true} color={'var(--primary-red)'}>
          {emailIssue}
        </Text.Medium>
      )}
      {emailSentSuccessfully && (
        <Column alignItems="stretch" style={{ flexGrow: 0 }}>
          <div className="bz df da"
          >
            <Text.Medium isBold={true} color={'var(--shades-blue-500)'}>
              {translations.verificationCodeBanner}
            </Text.Medium>{' '}
            <Button
              variant="link"
              label={translations.sendCodeAgain}
              onClick={onSendCodeAgain}
            />
          </div>
          <VerificationCodeInput
            disabled={isCodeCorrect}
            text={verificationCode}
            errorMessage={
              isCodeCorrect === false ? translations.otcIssueMessage : null
            }
            setText={setVerificationCode}
          />
        </Column>
      )}
      <Column alignItems="stretch" style={{ justifyContent: 'end' }}>
        <Button
          disabled={isEmailInvalid}
          label={translations.loginButton}
          onClick={emailSentSuccessfully ? verifyCode : verifyEmail}
        />
      </Column>
    </Column>
  )
}
